import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { Paragraph, YStack } from 'tamagui';

import { Heading } from '@/components/heading';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { ScreenHeader } from '@/components/screen-header';
import { useAuth } from '@/hooks/auth-context';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { borderWidth } from '@/theme/tokens.generated';

// Reachable from a profile, thread, or post (issue #17's own
// acceptance criterion -- see Task 10 for the three entry points).
// Single confirmation only: this screen IS the one confirmation, no
// additional "are you sure?" step on top -- issue #17's explicit
// "no dark patterns delaying or discouraging the block/report action"
// rule.
export function BlockUser({ userId }: { userId: string | undefined }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const currentUserId = auth.status === 'signed-up' ? auth.user.id : undefined;
  const [done, setDone] = useState(false);

  const block = useMutation({
    mutationFn: () => api.createBlock(userId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile(userId as string) });
      queryClient.invalidateQueries({ queryKey: queryKeys.revealRequests() });
      queryClient.invalidateQueries({ queryKey: queryKeys.connections() });
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
      queryClient.invalidateQueries({ queryKey: queryKeys.threads() });
      queryClient.invalidateQueries({ queryKey: queryKeys.thread() });
      setDone(true);
    },
  });

  const isSelf = currentUserId !== undefined && currentUserId === userId;

  return (
    <ScreenContainer>
      <YStack flex={1} pt="$8" gap="$6">
        <ScreenHeader onBack={() => router.back()} />

        {done ? (
          <YStack
            backgroundColor="$feedbackSuccessBg"
            borderColor="$feedbackSuccessBorder"
            borderWidth={borderWidth.hairline}
            borderRadius="$sm"
            p="$4"
            gap="$2"
          >
            <Heading>Blocked</Heading>
            {/* States what actually happened, not generic "thanks" --
                issue #17's explicit acceptance criterion. */}
            <Paragraph color="$feedbackSuccessText">
              You won&apos;t see posts or replies from each other anymore, and they can&apos;t send you a reveal
              request.
            </Paragraph>
            <PrimaryButton onPress={() => router.push('/')}>Done</PrimaryButton>
          </YStack>
        ) : isSelf ? (
          <Paragraph color="$destructive">You can&apos;t block yourself.</Paragraph>
        ) : (
          <YStack gap="$4">
            <Heading>Block this person?</Heading>
            <Paragraph color="$colorSecondary">
              You won&apos;t see their posts or replies, and they won&apos;t see yours. They can&apos;t send you a
              reveal request.
            </Paragraph>
            {block.isError && (
              <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
                {block.error.message || 'Something went wrong. Check your connection and try again.'}
              </Paragraph>
            )}
            <PrimaryButton
              variant="outlineDanger"
              loading={block.isPending}
              onPress={() => {
                if (!block.isPending) block.mutate();
              }}
            >
              Block
            </PrimaryButton>
          </YStack>
        )}
      </YStack>
    </ScreenContainer>
  );
}
